import React from 'react';
import { Box, Container, Typography, Paper, Divider, List, ListItem, ListItemText, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';

const PrivacyPolicyPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default, py: 8 }}>
      <Container maxWidth="md">
        <IconButton onClick={() => navigate('/')} sx={{ mb: 3 }}>
          <ArrowBackIcon /> <Typography sx={{ ml: 1 }}>{t('back_to_home')}</Typography>
        </IconButton>

        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            {t('privacy_policy_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            {t('last_updated')}: {new Date().toLocaleDateString()}
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Introduction */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('privacy_intro_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('privacy_intro_text')}
            </Typography>
          </Box>

          {/* Data Controller */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('data_controller_title')}
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Billeur</strong><br />
              Brussels, Belgium<br />
              Email: privacy@billeur.com<br />
              {t('vat_number')}: BE0123456789
            </Typography>
          </Box>

          {/* What Data We Collect */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('data_we_collect_title')}
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary={t('personal_data_title')}
                  secondary={t('personal_data_desc')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('business_data_title')}
                  secondary={t('business_data_desc')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('technical_data_title')}
                  secondary={t('technical_data_desc')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('usage_data_title')}
                  secondary={t('usage_data_desc')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            </List>
          </Box>

          {/* Legal Basis */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('legal_basis_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('legal_basis_text')}
            </Typography>
          </Box>

          {/* How We Use Data */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('how_we_use_data_title')}
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary={t('use_provide_service')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('use_process_transactions')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('use_improve_service')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('use_customer_support')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('use_comply_legal')} />
              </ListItem>
            </List>
          </Box>

          {/* Data Sharing */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('data_sharing_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('data_sharing_text')}
            </Typography>
          </Box>

          {/* Your Rights (GDPR) */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('your_rights_title')}
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary={t('right_access')}
                  secondary={t('right_access_desc')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('right_rectification')}
                  secondary={t('right_rectification_desc')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('right_erasure')}
                  secondary={t('right_erasure_desc')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('right_data_portability')}
                  secondary={t('right_data_portability_desc')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('right_object')}
                  secondary={t('right_object_desc')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('right_complain')}
                  secondary={t('right_complain_desc')}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            </List>
          </Box>

          {/* Data Retention */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('data_retention_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('data_retention_text')}
            </Typography>
          </Box>

          {/* Security */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('data_security_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('data_security_text')}
            </Typography>
          </Box>

          {/* International Transfers */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('international_transfers_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('international_transfers_text')}
            </Typography>
          </Box>

          {/* Contact */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              {t('contact_dpo_title')}
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              {t('contact_dpo_text')}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
